import type { SchoolSummary } from '@school-erp/types';
import { createSchoolSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { TextField } from '../../components/TextField';
import { TimezoneCurrencyLists } from '../../components/Lists';
import { t } from '../../i18n';

export function SchoolsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const schools = useQuery({
    queryKey: ['schools'],
    queryFn: () => api<SchoolSummary[]>('/schools'),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('schools.title')}</h1>
        {can('schools.create') && (
          <button
            type="button"
            aria-expanded={adding}
            onClick={() => setAdding((open) => !open)}
            className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900"
          >
            {adding ? t('common.cancel') : t('schools.add')}
          </button>
        )}
      </div>

      {notice && (
        <p role="status" className="rounded bg-green-50 p-3 text-sm text-green-800">
          {notice}
        </p>
      )}
      {adding && (
        <CreateSchoolForm
          onCreated={() => {
            setAdding(false);
            setNotice(t('schools.created'));
            void queryClient.invalidateQueries({ queryKey: ['schools'] });
          }}
        />
      )}

      {schools.isPending && <p role="status">{t('common.loading')}</p>}
      {schools.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}
      {schools.data && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {t('schools.name')}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t('schools.code')}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t('schools.city')}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t('schools.status')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {schools.data.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-600">
                    {t('schools.empty')}
                  </td>
                </tr>
              )}
              {schools.data.map((school) => (
                <tr key={school.id}>
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/schools/${school.id}`} className="text-brand-700 underline">
                      {school.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{school.code}</td>
                  <td className="px-4 py-3">{school.city ?? '—'}</td>
                  <td className="px-4 py-3">
                    {school.isActive ? t('users.active') : t('users.disabled')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateSchoolForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', code: '', city: '', countryCode: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: object) => api('/schools', { method: 'POST', body }),
    onSuccess: onCreated,
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'SCHOOL_CODE_TAKEN') {
        setErrors({ code: error.message });
      } else if (error instanceof ApiError && error.code === 'SCHOOL_NAME_TAKEN') {
        setErrors({ name: error.message });
      } else {
        setFormError(t('common.actionError'));
      }
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    // Empty optional fields are omitted so the server applies defaults.
    const candidate = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ''));
    const parsed = createSchoolSchema.safeParse(candidate);
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    create.mutate(parsed.data);
  }

  const bind = (name: keyof typeof form) => ({
    name,
    value: form[name],
    error: errors[name],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: e.target.value }),
  });

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
    >
      <TextField label={t('schools.name')} required {...bind('name')} />
      <TextField
        label={t('schools.code')}
        hint={t('schools.codeHint')}
        required
        {...bind('code')}
      />
      <TextField label={t('schools.city')} {...bind('city')} />
      <TextField label={t('schools.country')} maxLength={2} {...bind('countryCode')} />
      <div className="sm:col-span-2">
        {formError && (
          <p role="alert" className="mb-2 text-sm text-red-700">
            {formError}
          </p>
        )}
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-900 disabled:opacity-50"
        >
          {create.isPending ? t('common.saving') : t('schools.create')}
        </button>
      </div>
      <TimezoneCurrencyLists />
    </form>
  );
}
